import {
  BQ_FIELDS_REGEX,
  PROJECT_ID_REGEX,
} from "@/features/admin/integrations/components/GcpIntegrationForm/constants";
import useAuthenticatedUser from "@/hooks/useAuthenticatedUser";
import useGatekeeper from "@/hooks/useGatekeeper";
import { Input } from "@/types";
import { validateFileUpload } from "@/ui-lib/components/FileUploader";
import getMergeState from "@/utils/getMergeState";
import { useDebounce } from "@/utils/timers";
import { isCustomMetricDataType } from "@/utils/typeGuards";
import { CustomMetricDataType } from "@ternary/api-lib/constants/enums";
import { LabelMap } from "@ternary/api-lib/core/types";
import { ExportSourceConfig } from "@ternary/api-lib/core/types/GcpIntegration";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import copyText from "../copyText";
import useGetBigQueryDatasets from "../hooks/useGetBigQueryDatasets";
import useGetBigQueryMetadata from "../hooks/useGetBigQueryMetadata";
import useGetBigQueryTables from "../hooks/useGetBigQueryTables";
import CustomMetricForm from "./CustomMetricForm";

const BIG_QUERY_INPUT_TYPE = "BIG_QUERY_INPUT_TYPE";
const CSV_INPUT_TYPE = "CSV_INPUT_TYPE";

const BIG_QUERY_TABLE_ROW_LIMIT = 50_000;

export type BqMetadata = { rowCount: number; schema: Record<string, string>[] };

export type CustomMetricEntity = {
  id: string;
  tenantID: string;
  bigQueryConfig: ExportSourceConfig | null;
  createdAt: string;
  createdByID: string;
  errorMessage: string | null;
  location: string | null;
  name: string;
  schema: Record<string, CustomMetricDataType | null>;
  status: string;
  updatedAt: string | null;
  updatedByID: string | null;
};

interface State {
  bigQueryConfig: {
    bigQueryDatasetIDInput: Input<string | null>;
    bigQueryProjectIDInput: Input<string | null>;
    bigQueryTableIDInput: Input<string | null>;
  };
  bigQueryConfigError?: {
    datasetID?: boolean;
    projectID?: boolean;
    tableID?: boolean;
  };
  csvInput: Input<string | null>;
  inputType: string;
  nameInput: Input<string>;
  schemaInput: Input<Record<string, string | null>>;
  csvInputError?: string;
  enableBigQueryMetadataQuery: boolean;
}

const initialState: State = {
  bigQueryConfig: {
    bigQueryDatasetIDInput: {
      value: null,
      hasChanged: false,
      isValid: false,
    },
    bigQueryProjectIDInput: {
      value: null,
      hasChanged: false,
      isValid: false,
    },
    bigQueryTableIDInput: {
      value: null,
      hasChanged: false,
      isValid: false,
    },
  },
  bigQueryConfigError: undefined,
  csvInput: { value: null, hasChanged: false, isValid: false },
  inputType: CSV_INPUT_TYPE,
  nameInput: { value: "", hasChanged: false, isValid: false },
  schemaInput: { value: {}, hasChanged: false, isValid: false },
  csvInputError: undefined,
  enableBigQueryMetadataQuery: false,
};

interface Props {
  customMetrics: CustomMetricEntity[];
  externalLabelMap?: LabelMap;
  isLoadingCustomMetrics: boolean;
  selectedCustomMetric?: CustomMetricEntity;
  isProcessing: boolean;
  onInteraction: (interaction: CustomMetricForm.Interaction) => void;
}

export function CustomMetricFormContainer(props: Props): JSX.Element {
  const authenticatedUser = useAuthenticatedUser();
  const gatekeeper = useGatekeeper();

  //
  // State
  //
  const [state, setState] = useState<State>(
    props.selectedCustomMetric !== undefined
      ? {
          bigQueryConfig: {
            bigQueryDatasetIDInput: {
              value:
                props.selectedCustomMetric.bigQueryConfig?.datasetID ?? null,
              hasChanged: false,
              isValid: true,
            },
            bigQueryProjectIDInput: {
              value:
                props.selectedCustomMetric.bigQueryConfig?.projectID ?? null,
              hasChanged: false,
              isValid: true,
            },
            bigQueryTableIDInput: {
              value: props.selectedCustomMetric.bigQueryConfig?.tableID ?? null,
              hasChanged: false,
              isValid: true,
            },
          },
          csvInput: {
            value: null,
            hasChanged: false,
            isValid: true,
          },
          inputType: props.selectedCustomMetric.bigQueryConfig
            ? BIG_QUERY_INPUT_TYPE
            : CSV_INPUT_TYPE,
          nameInput: {
            value: props.selectedCustomMetric.name,
            hasChanged: false,
            isValid: true,
          },
          schemaInput: {
            value: props.selectedCustomMetric.schema,
            hasChanged: false,
            isValid: true,
          },
          csvInputError: undefined,
          enableBigQueryMetadataQuery: true,
        }
      : initialState
  );

  const mergeState = getMergeState(setState);

  const debouncedBigQueryProjectID = useDebounce(
    state.bigQueryConfig.bigQueryProjectIDInput?.value ?? null
  );
  const debouncedBigQueryDatasetID = useDebounce(
    state.bigQueryConfig.bigQueryDatasetIDInput?.value ?? null
  );

  //
  // Queries
  //

  const { data: bigQueryMetadata, isLoading: isLoadingBigQueryMetadata } =
    useGetBigQueryMetadata(
      {
        tenantID: authenticatedUser.tenant.id,
        datasetID: state.bigQueryConfig?.bigQueryDatasetIDInput?.value ?? "",
        projectID: state.bigQueryConfig?.bigQueryProjectIDInput?.value ?? "",
        tableID: state.bigQueryConfig?.bigQueryTableIDInput?.value ?? "",
      },
      {
        enabled:
          gatekeeper.canListCustomMetrics &&
          !!state.bigQueryConfig &&
          state.enableBigQueryMetadataQuery,
      }
    );

  const { data: bigQueryDatasets = [], isLoading: isLoadingBigQueryDatasets } =
    useGetBigQueryDatasets(
      {
        tenantID: authenticatedUser.tenant.id,
        projectID: debouncedBigQueryProjectID || "",
      },
      {
        enabled: !!debouncedBigQueryProjectID,
        retry: false,
        meta: {},
      }
    );

  const { data: bigQueryTables = [], isLoading: isLoadingBigQueryTables } =
    useGetBigQueryTables(
      {
        tenantID: authenticatedUser.tenant.id,
        projectID: debouncedBigQueryProjectID || "",
        datasetID: debouncedBigQueryDatasetID || "",
      },
      {
        enabled: !!debouncedBigQueryProjectID && !!debouncedBigQueryDatasetID,
        retry: false,
        meta: {},
      }
    );

  //
  // Effects
  //

  useEffect(() => {
    if (!bigQueryMetadata) return;

    mergeState({
      csvInput: { value: null, hasChanged: true, isValid: true },
      schemaInput: {
        value: bigQueryMetadata.schema.reduce((accum, value) => {
          return {
            ...accum,
            [value.name]: "",
          };
        }, {}),
        isValid: false,
        hasChanged: true,
      },
    });
  }, [bigQueryMetadata]);

  //
  // Interaction Handlers
  //

  function handleInteraction(interaction: CustomMetricForm.Interaction): void {
    switch (interaction.type) {
      case CustomMetricForm.INTERACTION_CANCEL_BUTTON_CLICKED: {
        handleCancel();
        break;
      }
      case CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED: {
        handleSubmit();
        break;
      }
      case CustomMetricForm.INTERACTION_VALIDATE_BUTTON_CLICKED_UPDATE: {
        validateBigQuerySchema();
        break;
      }
      case CustomMetricForm.INTERACTION_UPDATE_BIGQUERY_CONFIG: {
        handleChangeBigQueryConfigInput(
          interaction.fieldName,
          interaction.value
        );
        mergeState({
          enableBigQueryMetadataQuery: false,
        });
        break;
      }
      case CustomMetricForm.INTERACTION_UPDATE_NAME: {
        handleChangeName(interaction.value);
        break;
      }
      case CustomMetricForm.INTERACTION_FILE_UPLOAD: {
        handleFileUpload(interaction.file);
        break;
      }
      case CustomMetricForm.INTERACTION_UPDATE_SCHEMA: {
        handleSelectSchemaOption(interaction.header, interaction.value);
        break;
      }
      case CustomMetricForm.INTERACTION_UPDATE_FORM_TYPE: {
        mergeState({
          inputType: interaction.value,
          enableBigQueryMetadataQuery: false,
        });
        break;
      }
      case CustomMetricForm.INTERACTION_TOGGLE_SCHEMA_CHECKBOX: {
        handleToggleCheckbox(interaction.checked, interaction.headerKey);
        break;
      }
    }
  }

  function handleCancel(): void {
    props.onInteraction({
      type: CustomMetricForm.INTERACTION_CANCEL_BUTTON_CLICKED,
    });

    setState(initialState);
  }

  function handleChangeBigQueryConfigInput(
    fieldName: string,
    value: string | null
  ) {
    let isValid = false;
    let hasChanged = false;
    const error = {
      ...(state.bigQueryConfigError ?? {}),
    };
    let inputFieldName: keyof State["bigQueryConfig"] =
      "bigQueryProjectIDInput";

    switch (fieldName) {
      case "projectID": {
        isValid = PROJECT_ID_REGEX.test(value ?? "");
        error.projectID = !isValid && value !== null;
        hasChanged =
          props.selectedCustomMetric?.bigQueryConfig?.projectID !== value;
        break;
      }
      case "datasetID": {
        isValid = BQ_FIELDS_REGEX.test(value ?? "");
        inputFieldName = "bigQueryDatasetIDInput";
        error.datasetID = !isValid && value !== null;
        hasChanged =
          props.selectedCustomMetric?.bigQueryConfig?.datasetID !== value;
        break;
      }
      case "tableID": {
        isValid = BQ_FIELDS_REGEX.test(value ?? "");
        inputFieldName = "bigQueryTableIDInput";
        error.tableID = !isValid && value !== null;
        hasChanged =
          props.selectedCustomMetric?.bigQueryConfig?.tableID !== value;
        break;
      }
    }

    setState((currentState) => ({
      ...currentState,
      bigQueryConfig: {
        ...currentState.bigQueryConfig,
        [`${inputFieldName}`]: { value, isValid, hasChanged },
      },
      bigQueryConfigError: error,
    }));
  }

  function handleChangeName(value: string): void {
    const name = value.replace(/\s/g, "");

    const isValid = name.length > 0;

    const hasChanged = !isEqual(name, props.selectedCustomMetric?.name);

    mergeState({
      nameInput: { value: name, hasChanged, isValid },
    });
  }

  async function handleFileUpload(file: File | null): Promise<void> {
    const fileValidation = validateFileUpload(file, {
      acceptedFileTypes: ["CSV"],
    });

    if (fileValidation || file === null) {
      mergeState({
        csvInput: { value: null, hasChanged: false, isValid: false },
        schemaInput: { value: {}, hasChanged: false, isValid: false },
        csvInputError: file !== null ? fileValidation : undefined,
      });
      return;
    }

    const blobString = await file.text();

    const base64CSV = Buffer.from(blobString).toString("base64");

    const csvHeaders = blobString.split("\r\n")[0].split(",");

    mergeState({
      csvInput: {
        value: base64CSV,
        hasChanged: true,
        isValid: !fileValidation,
      },
      schemaInput: {
        value: csvHeaders.reduce((accum, header) => {
          if (accum[header]) return accum;

          accum[header] = "";

          return accum;
        }, {}),
        hasChanged: true,
        isValid: false,
      },
      csvInputError: fileValidation,
    });
  }

  function handleSelectSchemaOption(header: string, value: string): void {
    setState((currentState) => {
      const schemaValue = {
        ...currentState.schemaInput.value,
        [header]: value,
      };

      const [hasChanged, isValid] = validateSchema({
        externalLabelMap: props.externalLabelMap,
        schema: schemaValue,
        selectedCustomMetric: props.selectedCustomMetric,
      });

      return {
        ...currentState,
        schemaInput: {
          value: schemaValue,
          hasChanged,
          isValid,
        },
      };
    });
  }

  function handleSubmit(): void {
    const isCsvInput = state.inputType === CSV_INPUT_TYPE;

    if (props.selectedCustomMetric) {
      props.onInteraction({
        type: CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE,
        ...(hasChangedBigQueryConfig && !isCsvInput
          ? {
              bigQueryConfig: {
                datasetID:
                  state.bigQueryConfig.bigQueryDatasetIDInput.value ?? "",
                projectID:
                  state.bigQueryConfig.bigQueryProjectIDInput.value ?? "",
                tableID: state.bigQueryConfig.bigQueryTableIDInput.value ?? "",
              },
            }
          : {}),
        ...(state.csvInput.hasChanged && isCsvInput
          ? { csv: state.csvInput.value }
          : {}),
        ...(state.schemaInput.hasChanged
          ? { schema: getSchemaFromState(state.schemaInput.value) }
          : {}),
        ...(state.nameInput.hasChanged ? { name: state.nameInput.value } : {}),
      });
    } else {
      props.onInteraction({
        type: CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE,
        bigQueryConfig:
          isValidBigQueryConfig && !isCsvInput
            ? {
                datasetID:
                  state.bigQueryConfig.bigQueryDatasetIDInput.value ?? "",
                projectID:
                  state.bigQueryConfig.bigQueryProjectIDInput.value ?? "",
                tableID: state.bigQueryConfig.bigQueryTableIDInput.value ?? "",
              }
            : null,
        csv: state.csvInput.value,
        schema: getSchemaFromState(state.schemaInput.value),
        name: state.nameInput.value,
      });
    }
  }

  function handleToggleCheckbox(checked: boolean, headerKey: string) {
    setState((currentState) => {
      const schemaValue = {
        ...currentState.schemaInput.value,
        [headerKey]: checked ? "" : null,
      };

      const [hasChanged, isValid] = validateSchema({
        externalLabelMap: props.externalLabelMap,
        schema: schemaValue,
        selectedCustomMetric: props.selectedCustomMetric,
      });

      return {
        ...currentState,
        schemaInput: {
          ...currentState.schemaInput,
          hasChanged,
          isValid,
          value: schemaValue,
        },
      };
    });
  }

  function validateBigQuerySchema(): void {
    if (!isValidBigQueryConfig) {
      return;
    }

    setState((currentState) => ({
      ...currentState,
      enableBigQueryMetadataQuery: true,
    }));
  }

  //
  // Computed Values
  //
  const hasChanged = Object.values(state).some((input) => input?.hasChanged);

  const hasChangedBigQueryConfig =
    state.bigQueryConfig.bigQueryDatasetIDInput.hasChanged &&
    state.bigQueryConfig.bigQueryProjectIDInput.hasChanged &&
    state.bigQueryConfig.bigQueryTableIDInput.hasChanged;

  const isNameInputValid =
    !!props.selectedCustomMetric ||
    !props.customMetrics.some(
      (metric) =>
        metric.name.toLowerCase() === state.nameInput.value.toLowerCase()
    );

  const isValid = Object.entries(state).every(([key, input]) => {
    if (
      key === "inputType" ||
      key === "csvInputError" ||
      key === "bigQueryConfigError" ||
      key === "enableBigQueryMetadataQuery"
    ) {
      return true;
    }

    if (key === "bigQueryConfig") {
      if (state.inputType === BIG_QUERY_INPUT_TYPE) {
        return Object.values(input).every((input) => (input as Input).isValid);
      } else {
        return true;
      }
    }

    return input.isValid;
  });

  const isValidBigQueryConfig =
    state.bigQueryConfig.bigQueryDatasetIDInput.isValid &&
    state.bigQueryConfig.bigQueryProjectIDInput.isValid &&
    state.bigQueryConfig.bigQueryTableIDInput.isValid;

  const canSubmit = props.selectedCustomMetric
    ? hasChanged && isValid
    : isValid;

  const typeOptions = [
    {
      label: copyText.customMetricFormCsvInputTypeLabel,
      value: CSV_INPUT_TYPE,
    },
    {
      label: copyText.customMetricFormBigQueryInputTypeLabel,
      value: BIG_QUERY_INPUT_TYPE,
    },
  ];

  const selectedTypeOption = typeOptions.find(
    (option) => option.value === state.inputType
  );

  const bqTableRowCountExceeded =
    bigQueryMetadata && bigQueryMetadata.rowCount > BIG_QUERY_TABLE_ROW_LIMIT;

  return (
    <CustomMetricForm
      bigQueryConfig={state.bigQueryConfig}
      bigQueryConfigError={state.bigQueryConfigError}
      bigQueryDatasets={bigQueryDatasets}
      bigQueryTables={bigQueryTables}
      bqTableRowCountExceeded={bqTableRowCountExceeded}
      canSubmit={canSubmit}
      csvInput={state.csvInput}
      csvInputError={state.csvInputError}
      externalLabelMap={props.externalLabelMap}
      inputType={state.inputType}
      isLoadingBigQueryDatasets={isLoadingBigQueryDatasets}
      isLoadingBigQueryMetadata={isLoadingBigQueryMetadata}
      isLoadingBigQueryTables={isLoadingBigQueryTables}
      isNameInputValid={isNameInputValid}
      isProcessing={props.isProcessing}
      isValidBigQueryConfig={isValidBigQueryConfig}
      nameInput={state.nameInput}
      schemaInput={state.schemaInput}
      selectedCustomMetric={props.selectedCustomMetric}
      selectedTypeOption={selectedTypeOption}
      typeOptions={typeOptions}
      onInteraction={handleInteraction}
    />
  );
}

function getSchemaFromState(
  schema: Record<string, string | null>
): Record<string, CustomMetricDataType | null> {
  return Object.entries(schema).reduce(
    (accum, [key, value]) => ({
      ...accum,
      [key]: value && isCustomMetricDataType(value) ? value : null,
    }),
    {}
  );
}

function validateSchema(params: {
  externalLabelMap?: LabelMap;
  schema: Record<string, CustomMetricDataType | string | null>;
  selectedCustomMetric?: CustomMetricEntity;
}) {
  const schemaEntries = Object.entries(params.schema);

  const isValid =
    schemaEntries.every(([_, value]) =>
      value !== null ? isCustomMetricDataType(value) : true
    ) &&
    schemaEntries.every(([key, value]) =>
      value === CustomMetricDataType.DIMENSION
        ? !Object.keys(params.externalLabelMap ?? {}).some(
            (label) => label === key
          )
        : true
    ) &&
    schemaEntries.some(
      ([_, value]) => value === CustomMetricDataType.MEASURE
    ) &&
    schemaEntries.some(
      ([_, value]) => value === CustomMetricDataType.TIMESTAMP
    );

  const hasChanged = !isEqual(
    params.schema,
    params.selectedCustomMetric?.schema
  );

  return [hasChanged, isValid];
}
